"use client";

/** Checks the API-managed session and returns to login after any later 401. */
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Brand } from "@/components/dashboard-shell/Brand";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { LoginRequestSchema } from "@/contracts/auth";
import {
  AUTHENTICATION_REQUIRED_EVENT,
  PlatformApiError,
} from "@/lib/api";
import { useLogin } from "../data/auth.mutations";
import { useAuthSession } from "../data/auth.queries";
import { clearAuthenticatedQueries } from "../data/query-cache";
import styles from "./AuthGate.module.css";

function loginErrorMessage(error: unknown): string {
  if (!(error instanceof PlatformApiError)) {
    return "The dashboard could not be unlocked. Try again.";
  }

  if (error.status === 401 || error.status === 403) {
    return "That password is not recognized.";
  }

  return error.status === undefined
    ? "The dashboard could not be reached. Try again."
    : "The dashboard could not be unlocked. Try again.";
}

export function AuthGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const sessionQuery = useAuthSession();
  const loginMutation = useLogin();

  useEffect(() => {
    function requireAuthentication() {
      clearAuthenticatedQueries(queryClient);
    }

    window.addEventListener(AUTHENTICATION_REQUIRED_EVENT, requireAuthentication);

    return () => {
      window.removeEventListener(AUTHENTICATION_REQUIRED_EVENT, requireAuthentication);
    };
  }, [queryClient]);

  const authenticated = sessionQuery.data === true;
  const sessionError = sessionQuery.error;
  const visibleError =
    error ||
    (sessionError &&
    (!(sessionError instanceof PlatformApiError) || sessionError.status === undefined)
      ? "The TradingPlatform API could not be reached. Try again."
      : "");

  if (authenticated) return children;

  if (sessionQuery.isPending) {
    return (
      <main className={`${styles.page} ${styles.loadingPage}`} aria-label="Checking dashboard access">
        <Brand />
      </main>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");

    const credentials = LoginRequestSchema.safeParse({
      appKey: new FormData(event.currentTarget).get("password"),
    });

    if (!credentials.success) {
      setError("Enter your password.");
      return;
    }

    try {
      await loginMutation.mutateAsync(credentials.data);
    } catch (loginError) {
      setError(loginErrorMessage(loginError));
    }
  }

  return (
    <main className={styles.page}>
      <section className={styles.panel} aria-label="Dashboard access">
        <div className={styles.brand}>
          <Brand />
        </div>

        <div className={styles.copy}>
          <p>Enter your password to continue.</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <label htmlFor="dashboard-password">Password</label>
          <Input
            id="dashboard-password"
            name="password"
            type="password"
            autoComplete="current-password"
            autoFocus
            required
            aria-invalid={visibleError ? true : undefined}
            aria-describedby={visibleError ? "login-error" : undefined}
            disabled={loginMutation.isPending}
          />

          <p className={styles.error} id="login-error" aria-live="polite">
            {visibleError}
          </p>

          <Button
            className={styles.submit}
            type="submit"
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? "Checking…" : "Continue"}
          </Button>
        </form>
      </section>
    </main>
  );
}
